import type { FC } from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import ExcerptRenderer from '@wix/da-shared-react/pkg/TextRenderer/ExcerptRenderer';
import TextRenderer from '@wix/da-shared-react/pkg/TextRenderer';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import { MeasuredCookieType } from '@wix/da-hooks/pkg/useMeasureElement/redux/types';
import GallectionTiptapViewer from '@wix/da-editor-tiptap/pkg/components/Viewer/presets/Gallection';
import { isEditorTextEmpty } from '@wix/da-editor-tiptap/pkg/components/utils/helpers';
import { getDeviationResourceType } from '@wix/da-shared-react/pkg/DeviationViews/_helpers';

import s from './TierDescription.scss';

const COLLAPSE_HEIGHT = 137;
const COLLAPSE_HEIGHT_MOBILE = 110;

interface Props {
  className?: string;
  tier: PapiDeviation;
  onExpand?: (toggle: boolean) => void;
}

const TierDescription: FC<Props> = ({ className, tier, onExpand }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const [collapsedDescription, setCollapsedDescription] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (
      (isMobile && (descriptionRef.current?.clientHeight ?? 0) > COLLAPSE_HEIGHT_MOBILE) ||
      (descriptionRef.current?.clientHeight ?? 0) > COLLAPSE_HEIGHT
    ) {
      setCollapsedDescription(true);
    }
  }, [isMobile]);

  if (tier.textContent && isEditorTextEmpty(tier.textContent)) {
    return null;
  }
  return (
    <div className={cn(s['root'], !collapsedDescription && s['scrollable'], className)}>
      <div
        className={cn(
          s['collapsible-description'],
          collapsedDescription === true && s['collapsed-description'],
          collapsedDescription === false && s['expanded-description']
        )}
      >
        <div ref={descriptionRef}>
          {tier.textContent?.html.type === 'writer' && (
            <ExcerptRenderer
              htmlExcerptLegacy
              legacyTheme={'comments'}
              textContent={tier.textContent}
            />
          )}
          {tier.textContent?.html.type === 'tiptap' && (
            <TextRenderer
              tipTapPresetId="gallection"
              tipTapViewer={GallectionTiptapViewer}
              cookieType={MeasuredCookieType.GALLECTION_FOLDER}
              textContent={tier.textContent}
            />
          )}
        </div>
      </div>
      {(collapsedDescription === true || collapsedDescription === false) && (
        <PlainButtonOrLink
          className={s['expand-btn']}
          onClick={() => {
            setCollapsedDescription(!collapsedDescription);
            onExpand && onExpand(!!collapsedDescription);
          }}
          biData={
            collapsedDescription
              ? BiData<DefaultBiEvent>({
                  evid: 2,
                  itemid: tier.deviationId,
                  typeid: getDeviationResourceType(tier),
                  click_info: 'tier_description_show_more',
                })
              : undefined
          }
        >
          {t(!collapsedDescription ? 'common.showLess' : 'common.showMore')}
        </PlainButtonOrLink>
      )}
      {(!descriptionRef.current || collapsedDescription === undefined) && (
        /* CLS placeholder stops the elements below jumping between SSR and measurements */
        <div style={{ height: '20px' }}></div>
      )}
    </div>
  );
};
TierDescription.displayName = 'TierDescription';

export default TierDescription;
